import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useSnackbar } from 'notistack';
import SaveIcon from '@mui/icons-material/Save';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { getSiteSettings, updateSiteSettings, updateAdminCredentials } from '../services/api';

function Settings() {
  const { enqueueSnackbar } = useSnackbar();

  // Site settings
  const [siteSettings, setSiteSettings] = useState({
    site_title: '',
    site_description: ''
  });

  // Admin credentials
  const [credentialsForm, setCredentialsForm] = useState({
    username: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // UI states
  const [saving, setSaving] = useState(false);
  const [credentialsError, setCredentialsError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getSiteSettings();
        setSiteSettings({
          site_title: data.site_title,
          site_description: data.site_description || ''
        });
      } catch (error) {
        console.error('Error loading settings:', error);
        enqueueSnackbar('Failed to load site settings', { variant: 'error' });
      }
    };

    loadSettings();
  }, [enqueueSnackbar]);

  const handleSiteSettingsChange = (e) => {
    const { name, value } = e.target;
    setSiteSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCredentialsChange = (e) => {
    const { name, value } = e.target;
    setCredentialsForm(prev => ({
      ...prev,
      [name]: value
    }));
    setCredentialsError('');
  };

  const validateSiteSettings = () => {
    if (!siteSettings.site_title.trim()) {
      enqueueSnackbar('Website title is required', { variant: 'error' });
      return false;
    }
    return true;
  };

  const validateCredentialsForm = () => {
    if (!credentialsForm.current_password) {
      setCredentialsError('Current password is required');
      return false;
    }

    // If new password is provided, validate it
    if (credentialsForm.new_password) {
      if (credentialsForm.new_password.length < 8) {
        setCredentialsError('New password must be at least 8 characters long');
        return false;
      }

      if (credentialsForm.new_password !== credentialsForm.confirm_password) {
        setCredentialsError('New passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();

    if (!validateSiteSettings()) return;

    setSaving(true);
    try {
      await updateSiteSettings(siteSettings);
      enqueueSnackbar('Site settings updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating site settings:', error);
      enqueueSnackbar('Failed to update site settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();

    if (!validateCredentialsForm()) return;

    const updateData = {
      current_password: credentialsForm.current_password
    };

    if (credentialsForm.username.trim()) {
      updateData.username = credentialsForm.username;
    }

    if (credentialsForm.new_password) {
      updateData.new_password = credentialsForm.new_password;
    }

    setSaving(true);
    try {
      await updateAdminCredentials(updateData);
      enqueueSnackbar('Admin credentials updated successfully', { variant: 'success' });
      setCredentialsForm({
        username: '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error updating admin credentials:', error);
      let errorMessage = 'Failed to update admin credentials';
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = error.response.data.detail;
      }
      setCredentialsError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1" fontWeight="600" gutterBottom>
          WorldWise Scholar Settings
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveSettings}
          disabled={saving}
          startIcon={<SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Site Information
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Basic information about your website.
          </Typography>
          <form onSubmit={handleSaveSettings}>
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Site Title"
                name="site_title"
                value={siteSettings.site_title}
                onChange={handleSiteSettingsChange}
                margin="normal"
                variant="outlined"
                required
                helperText="The main title of your website (e.g., WorldWise Scholar)"
              />

              <TextField
                label="Website Description"
                name="site_description"
                value={siteSettings.site_description}
                onChange={handleSiteSettingsChange}
                fullWidth
                margin="normal"
                multiline
                rows={2}
                helperText="A short description about your website that appears on the homepage and in search results"
              />

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={saving}
                  startIcon={<SaveIcon />}
                >
                  {saving ? 'Saving...' : 'Save Site Info'}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Admin Credentials Section */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Admin Credentials
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {credentialsError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {credentialsError}
              </Alert>
            )}

            <form onSubmit={handleUpdateCredentials}>
              <TextField
                label="New Username"
                name="username"
                value={credentialsForm.username}
                onChange={handleCredentialsChange}
                fullWidth
                margin="normal"
                helperText="Leave blank to keep current username"
              />

              <TextField
                label="Current Password"
                name="current_password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={credentialsForm.current_password}
                onChange={handleCredentialsChange}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                label="New Password"
                name="new_password"
                type={showNewPassword ? 'text' : 'password'}
                value={credentialsForm.new_password}
                onChange={handleCredentialsChange}
                fullWidth
                margin="normal"
                helperText="Leave blank to keep current password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              {credentialsForm.new_password && (
                <TextField
                  label="Confirm New Password"
                  name="confirm_password"
                  type="password"
                  value={credentialsForm.confirm_password}
                  onChange={handleCredentialsChange}
                  fullWidth
                  margin="normal"
                  required={!!credentialsForm.new_password}
                />
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  color="secondary"
                >
                  {saving ? 'Updating...' : 'Update Credentials'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Grid>
    </Box>
  );
}

export default Settings;